import DefaultButton from '@/components/common/default/DefaultButton';
import DefaultLink from '@/components/common/default/DefaultLink';
import SkuTrendingBadge from '@/components/sku/SkuTrendingBadge';
import SkuDescription from '@/components/skuDescription';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import useWishlist from '@/hooks/useWishlist';
import { IndexSkuCardProps } from '@/types';
import { CommonEnum } from '@/types/translations/CommonEnum';
import { SkuMainImage } from '@/utilites/get-sku-main-image';
import { toDe } from '@/utilites/sku-operations';
import * as Tooltip from '@radix-ui/react-tooltip';
import { useEffect, useState } from 'react';
import { IoIosHeart, IoIosHeartEmpty } from 'react-icons/io';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useRouter } from 'next/router';
export default function IndexSkuCard({ sku, error = false }: IndexSkuCardProps) {
  const { locale } = useRouter();
  const [releaseDate, setReleaseDate] = useState<string | null>(null);
  const { isInWishlist, toggleFromWishlist } = useWishlist();
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const { translate: translateCommon } = useTypedTranslation('common');

  useEffect(() => {
    if (sku) {
      const currentDate = new Date();
      const skuReleaseDate = sku.releaseDate ? new Date(sku.releaseDate) : null;
      setReleaseDate(
        skuReleaseDate && skuReleaseDate.getTime() > currentDate.getTime() ? sku.releaseDate : null
      );
    }
  }, [sku]);

  if (error) {
    return null;
  }

  if (!sku) {
    return (
      <div className="tw-relative tw-rounded-[20px] tw-border-0.75 tw-border-solid tw-border-custom-gray tw-bg-white tw-p-4 tw-shadow-md">
        <div className="tw-absolute tw-right-5 tw-top-3">
          <Skeleton circle width={26} height={26} />
        </div>
        <div className="tw-mb-4 tw-block tw-w-full">
          <Skeleton
            height={271}
            className="tw-w-full tw-object-contain tw-p-1 max-nine:tw-h-[130px] max-nine:tw-w-[144px]"
          />
        </div>
        <div className="tw-flex tw-flex-grow tw-flex-col tw-justify-between">
          <div className="tw-relative tw-flex tw-min-h-[80px] tw-flex-col tw-items-start tw-gap-1 max-nine:tw-min-h-[40px]">
            <Skeleton width="80%" height={24} className="tw-mb-2" />
            <div className="tw-flex tw-flex-col max-nine:tw-min-h-[60px]">
              <Skeleton width="100%" height={16} className="tw-mb-1" />
            </div>
          </div>

          <div className="max-nine:tw-mt-2">
            <Skeleton width="40%" height={16} className="tw-mb-1" />
            <div className="tw-flex tw-w-full tw-items-center tw-justify-between">
              <Skeleton width="30%" height={22} />
              <Skeleton width="20%" height={22} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tw-relative tw-rounded-[20px] tw-border-0.75 tw-border-solid tw-border-custom-gray tw-bg-white tw-p-4 tw-shadow-md">
      <DefaultButton
        onClick={() => toggleFromWishlist(sku)}
        className="tw-absolute tw-right-5 tw-top-3 !tw-text-[26px] max-nine:!tw-text-xl"
      >
        {isInWishlist(sku.slug) ? (
          <IoIosHeart className="tw-text-red-500" />
        ) : (
          <IoIosHeartEmpty className="tw-text-slate-gray" />
        )}
      </DefaultButton>
      {releaseDate && (
        <Tooltip.Provider>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <div className="tw-absolute tw-left-2 tw-top-2 tw-z-20 tw-cursor-pointer tw-rounded-lg tw-bg-custom-gray tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-text">
                <span>{new Date(releaseDate).toLocaleString(locale, { month: 'short' })}</span>
                <span className="tw-mx-2 tw-text-text/20">|</span>
                <span>{new Date(releaseDate).getDate()}</span>
              </div>
            </Tooltip.Trigger>
            <Tooltip.Content
              className="tw-rounded-md tw-bg-black tw-px-2 tw-py-1 tw-text-sm tw-text-white tw-shadow-lg"
              side="top"
              sideOffset={5}
            >
              {new Date(releaseDate).toLocaleDateString(locale, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
              <Tooltip.Arrow className="tw-fill-black" />
            </Tooltip.Content>
          </Tooltip.Root>
        </Tooltip.Provider>
      )}
      <DefaultLink href={`/${sku.slug}`} className="tw-mb-4 tw-block tw-w-full">
        <SkuMainImage
          src={sku.mainImageUrl}
          alt={sku.name || ''}
          width={300}
          height={271}
          className="tw-w-full tw-object-contain tw-p-1 max-nine:tw-h-[130px] max-nine:tw-w-[144px]"
        />
      </DefaultLink>
      <div className="tw-flex tw-flex-grow tw-flex-col tw-justify-between">
        <div className="tw-relative tw-flex tw-min-h-[80px] tw-flex-col tw-items-start tw-gap-1 max-nine:tw-min-h-[40px]">
          <DefaultLink href={`/${sku.slug}`} className="max-nine:tw-hidden">
            <Tooltip.Provider>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <p className="tw-m-0 tw-line-clamp-2 tw-cursor-default tw-p-0 tw-text-left tw-text-base tw-font-bold tw-text-textBlack max-nine:tw-pr-2 max-nine:tw-text-base">
                    {sku.name}
                  </p>
                </Tooltip.Trigger>
                <Tooltip.Content
                  className="tw-rounded-md tw-bg-black tw-px-2 tw-py-1 tw-text-sm tw-text-white tw-shadow-lg"
                  side="top"
                  sideOffset={5}
                >
                  {sku.name}
                  <Tooltip.Arrow className="tw-fill-black" />
                </Tooltip.Content>
              </Tooltip.Root>
            </Tooltip.Provider>
          </DefaultLink>
          <div className={`tw-flex tw-flex-col max-nine:tw-min-h-[60px]`}>
            <DefaultButton
              onClick={() => {
                setTooltipVisible(!tooltipVisible);
                setTimeout(() => {
                  setTooltipVisible(false);
                }, 2000);
              }}
              className="nine:tw-hidden"
            >
              <p className="tw-m-0 tw-line-clamp-2 tw-p-0 tw-text-left tw-text-base tw-font-semibold tw-text-textBlack max-nine:tw-pr-2 max-nine:tw-text-sm">
                {sku.name}
              </p>
            </DefaultButton>
            {tooltipVisible && (
              <div className="tw-absolute tw-left-0 tw-top-full tw-z-20 tw-mt-1 tw-rounded-md tw-bg-black tw-px-3 tw-py-2 tw-text-xs tw-text-white tw-shadow-lg">
                {sku.name}
              </div>
            )}
            <p className="tw-m-0 tw-whitespace-nowrap tw-p-0 tw-text-left tw-text-sm tw-font-normal tw-text-muted max-nine:tw-text-[12px] nine:tw-mb-2">
              <SkuDescription
                skuBottleSizeInMl={sku.bottleSizeInMl}
                skuReleaseDate={sku.releaseDate}
                skuVolumeInPercent={sku.volumeInPercent}
              />
            </p>
          </div>
        </div>

        <DefaultLink href={`/${sku.slug}`}>
          <div className="max-nine:tw-mt-2">
            <p className="tw-m-0 tw-p-0 tw-pt-1 tw-text-left tw-text-sm tw-font-normal tw-leading-normal tw-text-muted max-nine:tw-text-xs-lower">
              {translateCommon(CommonEnum['lowest_ask'])}
            </p>
            <div className="tw-flex tw-w-full tw-items-center tw-justify-between">
              <p className="max-nine: tw-mb-0 tw-text-[22px] tw-font-normal tw-text-textBlack max-nine:tw-text-base max-nine:tw-font-[600]">
                {!sku.lowestAsk ? '--' : toDe(sku.lowestAsk ?? 0)}
              </p>
              <SkuTrendingBadge sku={sku} />
            </div>
          </div>
        </DefaultLink>
      </div>
    </div>
  );
}
